import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clearSessionsApi, deleteSessionApi, getSessionsApi, type SessionItem } from '../api';
import UiIcon from '../UiIcon';
import { groupHistory, historyTitle, historyTime } from '../lib/history';
import { toast } from '../toast';
import './History.css';

function TrashIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7" /></svg>;
}

function NewConversation() {
  return <Link className="history-new" to="/chat"><span aria-hidden="true">＋</span> 发起新对话</Link>;
}

export default function HistoryPage() {
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const busy = clearing || pendingId !== null;
  const groups = useMemo(() => groupHistory(sessions, query), [sessions, query]);
  const matchCount = groups.reduce((total, group) => total + group.items.length, 0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    getSessionsApi()
      .then((items) => {
        if (!Array.isArray(items)) throw new Error('Invalid session list');
        if (alive) setSessions(items);
      })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reload]);

  async function removeSession(id: string) {
    if (busy || !window.confirm('删除这条历史对话？删除后无法恢复。')) return;
    setPendingId(id);
    try {
      await deleteSessionApi(id);
      setSessions((items) => items.filter((item) => item.id !== id));
      toast('历史对话已删除');
    } catch {
      toast('删除失败，请稍后重试');
    } finally {
      setPendingId(null);
    }
  }

  async function clearHistory() {
    // The list endpoint returns the latest 30; clearing applies to all history.
    if (busy || !window.confirm('确认清空全部历史对话？包括未在此页显示的记录，删除后无法恢复。')) return;
    setClearing(true);
    try {
      await clearSessionsApi();
      setSessions([]);
      setQuery('');
      toast('历史对话已清空');
    } catch {
      toast('清空失败，请稍后重试');
    } finally {
      setClearing(false);
    }
  }

  return (
    <main className="container history-page">
      <header className="history-header">
        <div className="history-heading">
          <span className="history-mark"><UiIcon name="history" /></span>
          <div>
            <h1>我的对话</h1>
            <p>对话自动保存，随时回来接着聊。</p>
          </div>
        </div>
        <NewConversation />
      </header>

      <section className="history-panel" aria-label="历史会话列表" aria-busy={loading}>
        <div className="history-toolbar">
          <div className="history-search">
            <UiIcon name="search" />
            <input type="search" aria-label="搜索最近对话" placeholder="搜索最近对话的标题" value={query} onChange={(event) => setQuery(event.target.value)} disabled={loading || failed || sessions.length === 0} />
            {query && <button type="button" className="history-search-clear" aria-label="清除搜索" onClick={() => setQuery('')}>×</button>}
          </div>
          {sessions.length > 0 && !loading && !failed && <button type="button" className="history-clear" onClick={() => void clearHistory()} disabled={busy}><TrashIcon />{clearing ? '清空中…' : '清空全部'}</button>}
        </div>

        {loading ? (
          <div className="history-loading" role="status">
            <span className="history-state-label">正在加载历史对话…</span>
            {[0, 1, 2, 3].map((n) => <div className="history-skeleton" key={n} aria-hidden="true"><i /><div><b /><span /></div></div>)}
          </div>
        ) : failed ? (
          <div className="history-empty" role="alert">
            <span className="history-empty-icon"><UiIcon name="history" /></span>
            <h2>暂时没能加载历史对话</h2>
            <p>请检查网络连接后重试。</p>
            <button type="button" className="history-retry" onClick={() => setReload((n) => n + 1)}>重新加载</button>
          </div>
        ) : sessions.length === 0 ? (
          <div className="history-empty">
            <span className="history-empty-icon"><UiIcon name="chat" /></span>
            <h2>还没有历史对话</h2>
            <p>从一个感兴趣的问题开始，回答和资料出处会保存在这里。</p>
            <NewConversation />
          </div>
        ) : (
          <>
            <div className="history-summary" role="status">
              <span>{query.trim() ? `找到 ${matchCount} 条对话` : `最近 ${sessions.length} 条对话`}</span>
              <span>{query.trim() ? '在最近对话中搜索' : '按最近更新排序'}</span>
            </div>
            {matchCount === 0 ? (
              <div className="history-empty">
                <span className="history-empty-icon"><UiIcon name="search" /></span>
                <h2>没有找到相关对话</h2>
                <p>换个标题关键词，或返回查看最近的对话。</p>
                <button type="button" className="history-retry" onClick={() => setQuery('')}>查看最近对话</button>
              </div>
            ) : groups.map((group) => (
              <section className="history-group" key={group.label} aria-label={group.label}>
                <h2 className="history-group-title">{group.label}<span>{group.items.length}</span></h2>
                <ul className="history-list">
                  {group.items.map((session) => {
                    const title = historyTitle(session.title);
                    return <li className="history-row" key={session.id}>
                      <Link className="history-open" to={`/chat?session=${encodeURIComponent(session.id)}`}>
                        <span className="history-row-icon"><UiIcon name="chat" /></span>
                        <div className="history-row-content">
                          <h3 title={title}>{title}</h3>
                          <div className="history-meta"><time dateTime={session.updated_at || undefined}>{historyTime(session.updated_at)}</time><span aria-hidden="true">·</span><span>{session.turns} 条消息</span></div>
                        </div>
                        <span className="history-continue">继续对话 <span aria-hidden="true">→</span></span>
                      </Link>
                      <button className="history-delete" type="button" aria-label={`删除会话：${title}`} title={pendingId === session.id ? '删除中…' : '删除会话'} disabled={busy} onClick={() => void removeSession(session.id)}><TrashIcon /></button>
                    </li>;
                  })}
                </ul>
              </section>
            ))}
          </>
        )}
      </section>
      <div className="footer">©2026 上海渔联网科技 · 科普趣答</div>
    </main>
  );
}
